<article class="doc">
  <header class="doc__header">
    <p class="doc__eyebrow">Atom</p>
    <h1 class="doc__title">Switch</h1>
    <p class="doc__lead">
      Toggle con thumb deslizante para activar o desactivar una opción booleana.
      Usa <code>role="switch"</code> para accesibilidad. Track inactivo <code>#cbd5e1</code>,
      activo <code>#4169e2</code>. Thumb siempre blanco con transición <code>0.2s</code>.
    </p>
  </header>

  <!-- ── Estados ─────────────────────────────── -->
  <h2 class="doc-section-title">Estados</h2>

  <div class="doc-grid">
    <app-example-section title="Apagado" description="Estado inicial por defecto.">
      <ui-switch label="Notificaciones" />
    </app-example-section>

    <app-example-section title="Encendido" description="checked=true.">
      <ui-switch label="Modo oscuro" [checked]="true" />
    </app-example-section>

    <app-example-section
      title="Error / inválido"
      description="Track en rojo. showMessage controla el texto de error."
    >
      <div class="doc-example-stack">
        <ui-switch
          label="Acepto condiciones"
          [required]="true"
          [invalid]="isInvalid()"
          message="Debes activar esta opción para continuar."
          [showMessage]="showErrorMsg()"
          [(checked)]="notifications"
        />
        <div class="doc-toggle-row">
          <button type="button" class="doc-native-toggle" (click)="toggleInvalid()">
            {{ isInvalid() ? 'Quitar error' : 'Activar error' }}
          </button>
          <button type="button" class="doc-native-toggle" (click)="toggleErrorMsg()">
            {{ showErrorMsg() ? 'Ocultar mensaje' : 'Mostrar mensaje' }}
          </button>
        </div>
      </div>
    </app-example-section>

    <app-example-section title="Deshabilitado apagado" description="Sin interacción.">
      <ui-switch label="Sincronización" [disabled]="true" />
    </app-example-section>

    <app-example-section title="Deshabilitado encendido" description="Valor fijo, sin interacción.">
      <ui-switch label="Configuración de sistema" [checked]="true" [disabled]="true" />
    </app-example-section>
  </div>

  <!-- ── Variantes ───────────────────────────── -->
  <h2 class="doc-section-title">Variantes</h2>

  <div class="doc-grid">
    <app-example-section
      title="Con descripción"
      description="Texto secundario bajo el label."
    >
      <ui-switch
        label="Notificaciones push"
        description="Recibirás alertas en tiempo real en tu dispositivo."
      />
    </app-example-section>

    <app-example-section
      title="Con badge Obligatorio"
      description="Indica que la activación es requerida."
    >
      <ui-switch label="Acepto la política de privacidad" [required]="true" />
    </app-example-section>

    <app-example-section
      title="Binding bidireccional"
      description="Estado reactivo con signal."
    >
      <div class="doc-example-stack">
        <ui-switch label="Modo oscuro" [(checked)]="darkMode" />
        <p class="doc-value-preview">
          Estado: <strong>{{ darkMode() ? 'encendido' : 'apagado' }}</strong>
        </p>
      </div>
    </app-example-section>

    <app-example-section
      title="Sin label"
      description="Solo el track visible, accesible vía aria-label."
    >
      <ui-switch />
    </app-example-section>
  </div>

  <!-- ── Ejemplos de uso ─────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Ejemplos de uso</h2>

    <p class="doc-block__p">Binding bidireccional con signal:</p>
    <pre class="doc-block__pre" role="code"><code>darkMode = signal(false);

&lt;ui-switch
  label="Modo oscuro"
  [(checked)]="darkMode"
/&gt;</code></pre>

    <p class="doc-block__p">Con Reactive Forms:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-switch
  label="Acepto términos"
  [required]="true"
  [checked]="form.controls.terms.value ?? false"
  (checkedChange)="form.controls.terms.setValue($event)"
  [invalid]="form.controls.terms.invalid &amp;&amp; form.controls.terms.touched"
  message="Debes activar esta opción."
/&gt;</code></pre>

    <p class="doc-block__p">Con descripción y validación silenciosa:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-switch
  label="Notificaciones"
  description="Activa para recibir alertas en tiempo real."
  [required]="true"
  [invalid]="hasError()"
  message="Esta opción es requerida."
  [showMessage]="submitted()"
/&gt;</code></pre>
  </section>

  <!-- ── Inputs ──────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Inputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Default</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr><td><code>checked</code></td><td><code>boolean</code> (model)</td><td><code>false</code></td><td>Estado del switch. Soporta <code>[(checked)]</code>.</td></tr>
        <tr><td><code>label</code></td><td><code>string</code></td><td><code>''</code></td><td>Texto principal junto al track.</td></tr>
        <tr><td><code>description</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Texto secundario bajo el label.</td></tr>
        <tr><td><code>required</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Badge "Obligatorio" y <code>aria-required</code>.</td></tr>
        <tr><td><code>showOptionalHint</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Badge "Opcional".</td></tr>
        <tr><td><code>disabled</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Deshabilita el switch.</td></tr>
        <tr><td><code>invalid</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Track en rojo y <code>aria-invalid</code>.</td></tr>
        <tr><td><code>message</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Texto del mensaje de error.</td></tr>
        <tr><td><code>showMessage</code></td><td><code>boolean</code></td><td><code>true</code></td><td>Controla si el mensaje se renderiza.</td></tr>
        <tr><td><code>name</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Atributo <code>name</code> del input.</td></tr>
        <tr><td><code>fieldId</code></td><td><code>string | undefined</code></td><td>Auto-generado</td><td>ID del input para la asociación con el label.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ── Outputs ─────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Outputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr><td><code>checkedChange</code></td><td><code>boolean</code></td><td>Emite el nuevo estado al cambiar.</td></tr>
        <tr><td><code>blurred</code></td><td><code>FocusEvent</code></td><td>Emite cuando el input pierde el foco.</td></tr>
        <tr><td><code>focused</code></td><td><code>FocusEvent</code></td><td>Emite cuando el input recibe el foco.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ── Buenas prácticas ───────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Buenas prácticas</h2>
    <ul class="doc-list">
      <li>Usa switch para cambios inmediatos que surten efecto al instante (modo oscuro, notificaciones).</li>
      <li>Usa checkbox cuando la selección solo aplica al enviar un formulario (aceptar términos).</li>
      <li>Siempre incluye un <code>label</code> descriptivo o un <code>aria-label</code> externo.</li>
      <li>Activa <code>[invalid]</code> solo tras interacción del usuario, no de forma anticipada.</li>
      <li>Usa <code>[showMessage]="false"</code> para marcar el error visualmente sin texto (espacios compactos).</li>
    </ul>
  </section>
</article>
